import type { SafetyFieldConfig, SafetySectionDefinition } from './types';
import { unitFieldTooltips } from '@/features/companies/components/masterDataFieldTooltips';
import { generateSafetyFormSections } from '../../utils/safetyDisplayHelpers';

/**
 * Section definitions for Emergenza (Emergency & Continuity) sub-tab.
 *
 * Note: Emergency contacts (personnel) are handled manually as they come from
 * a different data source.
 */
export const emergenzaSections: Record<string, SafetySectionDefinition> = {
  isolation: { title: 'Isolamento e sgancio', order: 1 },
  communications: { title: 'Comunicazioni', order: 2 },
  power: { title: 'Alimentazione e backup', order: 3 },
  safety: { title: 'Sicurezze impianto', order: 4 },
  valves: { title: 'Valvole di sicurezza', order: 5 },
} as const;

/**
 * Field definitions for Emergenza (Emergency & Continuity) sub-tab.
 */
export const emergenzaFields: readonly SafetyFieldConfig[] = [
  // Isolation section
  {
    key: 'emergencyElettroserraturaPresent',
    dataPath: 'technical.safety.emergencySystems.elettroserraturaPresent',
    label: 'Elettroserratura',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyElettroserraturaPresent,
    panel: 'isolation',
    section: 'isolation',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencySgancioAutomaticoPresent',
    dataPath: 'technical.safety.emergencySystems.sgancioAutomaticoPresent',
    label: 'Sgancio automatico',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.sgancioAutomaticoPresent,
    panel: 'isolation',
    section: 'isolation',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencySogliaSgancioAutomatico',
    dataPath: 'technical.safety.emergencySystems.sogliaSgancioAutomatico',
    label: 'Soglia sgancio',
    type: 'text',
    placeholder: 'Es: 300 ppm',
    tooltip: unitFieldTooltips.safety.sogliaSgancioAutomatico,
    panel: 'isolation',
    section: 'isolation',
  },
  {
    key: 'emergencyPulsanteSgancioPresent',
    dataPath: 'technical.safety.emergencySystems.pulsanteSgancioPresent',
    label: 'Pulsante sgancio',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.pulsanteSgancioPresent,
    panel: 'isolation',
    section: 'isolation',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencyEeTripButtons',
    dataPath: 'technical.safety.emergencySystems.eeTripButtons',
    label: 'Sgancio pulsanti EE',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyEeTripButtons,
    panel: 'isolation',
    section: 'isolation',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencyEeTripDetector',
    dataPath: 'technical.safety.emergencySystems.eeTripDetector',
    label: 'Sgancio da rilevatori',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyEeTripDetector,
    panel: 'isolation',
    section: 'isolation',
    displayFormatter: 'boolean',
  },
  // Communications section
  {
    key: 'emergencyDialerPresent',
    dataPath: 'technical.safety.emergencySystems.dialerPresent',
    label: 'Combinatore',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyDialerPresent,
    panel: 'communications',
    section: 'communications',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencyGuardServicePresent',
    dataPath: 'technical.safety.emergencySystems.guardServicePresent',
    label: 'Guardiania',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyGuardServicePresent,
    panel: 'communications',
    section: 'communications',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencyNotes',
    dataPath: 'technical.safety.emergencySystems.notes',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: unitFieldTooltips.safety.emergencyNotes,
    panel: 'communications',
    section: 'communications',
    colSpan: 2,
  },
  // Power section
  {
    key: 'powerPreferredLine',
    dataPath: 'technical.safety.powerBackup.preferredLine',
    label: 'Linea preferenziale',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.powerPreferredLine,
    panel: 'power',
    section: 'power',
    displayFormatter: 'boolean',
  },
  {
    key: 'powerUps',
    dataPath: 'technical.safety.powerBackup.ups',
    label: 'UPS',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.powerUps,
    panel: 'power',
    section: 'power',
    displayFormatter: 'boolean',
  },
  {
    key: 'powerGenerator',
    dataPath: 'technical.safety.powerBackup.generator',
    label: 'Generatore',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.powerGenerator,
    panel: 'power',
    section: 'power',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencyGeneratorPresent',
    dataPath: 'technical.safety.emergencySystems.generatorPresent',
    label: 'Gruppo elettrogeno',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyGeneratorPresent,
    panel: 'power',
    section: 'power',
    displayFormatter: 'boolean',
  },
  {
    key: 'powerNotes',
    dataPath: 'technical.safety.powerBackup.notes',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: unitFieldTooltips.safety.powerNotes,
    panel: 'power',
    section: 'power',
    colSpan: 2,
  },
  // Safety section
  {
    key: 'emergencyLightingPresent',
    dataPath: 'technical.safety.emergencySystems.emergencyLightingPresent',
    label: 'Illuminazione emergenza',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyLightingPresent,
    panel: 'safety',
    section: 'safety',
    displayFormatter: 'boolean',
  },
  {
    key: 'emergencyLightingAtex',
    dataPath: 'technical.safety.emergencySystems.emergencyLightingAtex',
    label: 'Illuminazione ATEX',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.emergencyLightingAtex,
    panel: 'safety',
    section: 'safety',
    displayFormatter: 'boolean',
  },
  // Valves section
  {
    key: 'valvesReliefSetPressuresBar',
    dataPath: 'technical.safety.valves.reliefSetPressuresBar',
    label: 'Pressioni taratura',
    type: 'text', // Array - formatted specially
    placeholder: 'Es: 12, 18',
    tooltip: unitFieldTooltips.safety.valvesReliefPressures,
    panel: 'valves',
    section: 'valves',
    unit: 'bar', // Used for array formatting
  },
  {
    key: 'valvesReliefPeriodicityYears',
    dataPath: 'technical.safety.valves.reliefPeriodicityYears',
    label: 'Periodicità prova',
    type: 'number',
    placeholder: 'Es: 2',
    tooltip: unitFieldTooltips.safety.valvesReliefPeriodicity,
    panel: 'valves',
    section: 'valves',
    unit: 'anni',
  },
  {
    key: 'valvesIsolationPresent',
    dataPath: 'technical.safety.valves.isolationPresent',
    label: 'Valvole intercettazione',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.valvesIsolationPresent,
    panel: 'valves',
    section: 'valves',
    displayFormatter: 'boolean',
  },
  {
    key: 'valvesIsolationDiverter',
    dataPath: 'technical.safety.valves.isolationDiverter',
    label: 'Deviatori installati',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.valvesIsolationDiverter,
    panel: 'valves',
    section: 'valves',
    displayFormatter: 'boolean',
  },
] as const;

/**
 * Form sections derived from field definitions.
 */
export const emergenzaFormSections = generateSafetyFormSections(
  emergenzaFields,
  ['isolation', 'communications', 'power', 'safety', 'valves'],
  {
    isolation: 'Isolamento e sgancio',
    communications: 'Comunicazioni',
    power: 'Alimentazione e backup',
    safety: 'Sicurezze impianto',
    valves: 'Valvole di sicurezza',
  }
);
